import { ICallSettingsAdapter } from "@webex/component-adapter-interfaces/dist/esm/src";
import { t } from "i18next";
import { from, Observable, ReplaySubject } from "rxjs";
import { CALL_FORWARDING, MAKE_A_CALL_KEYS, RESPONSE_STATUS } from "../../../../adapter-interfaces/src";
import { removeLocalStorageItem, setLocalStorageItem } from "../../../../logger/src/LocalStorage";
import { Logger } from "../../../../logger/src/Logger";
import { formatError } from "../../../../logger/src";
import { getAttendantConsoleFeatuteToggle, getCallSetting, getCXEssentialsFeature } from "../../services/WebexCallSettingsService";
import { submitMetrics } from "../../services/WebexMetricsService";

/**
 * Maximum number of automatic retry attempts for a single refresh cycle.
 * The first attempt is "attempt 0"; with MAX_RETRY_ATTEMPTS = 2 we issue at most
 * 1 immediate try + 2 retries = 3 total network calls per refresh.
 */
const MAX_RETRY_ATTEMPTS = 2;

/** Base delay (ms) for exponential backoff between retries: 1s, 2s. */
const RETRY_BASE_DELAY_MS = 1000;

/**
 * HTTP status codes treated as transient — worth retrying once or twice during
 * iframe init (token race), brief network hiccups, and rate limits.
 * Permanent codes (400/403/404/422) are surfaced immediately without retry.
 */
const RETRYABLE_STATUS_CODES: ReadonlySet<number> = new Set<number>([
    RESPONSE_STATUS.STATUSCODE_401 as number,
    RESPONSE_STATUS.STATUSCODE_408 as number,
    RESPONSE_STATUS.STATUSCODE_429 as number,
    RESPONSE_STATUS.STATUSCODE_500 as number,
    RESPONSE_STATUS.STATUSCODE_502 as number,
    RESPONSE_STATUS.STATUSCODE_503 as number,
    RESPONSE_STATUS.STATUSCODE_504 as number,
]);

/**
 * Adapter that exposes the user's call-forward state as an Observable<string>
 * for the React UI. The emitted string is one of:
 *   - a phone number (e.g. "+12345550100")        → forwarded to that number
 *   - the literal "VOICEMAIL"                     → forwarded to voicemail
 *   - the localized "Do not forward calls" text   → forwarding is OFF
 *
 * Critically, when the underlying API call fails (transient 401/429/5xx,
 * network error, or empty body), the adapter does NOT silently emit the
 * "Do not forward calls" text — that would misrepresent server state. Instead
 * it preserves the last known good value and retries quietly in the background.
 */
export class WebexCallSettingsAdapter implements ICallSettingsAdapter {
    observables$!: ReplaySubject<string>;

    /**
     * Last successfully resolved value, used to preserve UI state on transient
     * failure so we never flash the wrong "Do not forward calls" pill.
     */
    private lastSuccessValue?: string;

    /** Mutex to coalesce overlapping refreshCallForward() calls into one. */
    private inflight = false;

    /** Normalize API value so UI shows Voicemail correctly (API may return "voicemail", "Voicemail", or "VOICEMAIL"). */
    parseCallForwardData(response: string) {
        if (!response) return t("donotForwardCalls") as string;
        const trimmed = response.trim();
        if (trimmed.toUpperCase() === 'VOICEMAIL') return 'VOICEMAIL';
        return trimmed;
    }

    refreshCallForward(): void {
        if (this.inflight) {
            Logger.info(`WebexCallForwardService: refresh already in flight, skipping duplicate request`);
            return;
        }
        this.inflight = true;
        // Fire-and-forget: errors are handled inside executeRefresh so this never throws.
        this.executeRefresh(0).finally(() => {
            this.inflight = false;
        });
    }

    /**
     * Single attempt at fetching call-forward state, with bounded retry on
     * transient failures. All failure paths funnel through preserveOrEmitInitial
     * so the UI never gets a misleading empty/"do not forward" state on error.
     */
    private async executeRefresh(attempt: number): Promise<void> {
        const startTime = Date.now();
        try {
            const data = await getCallSetting();
            const latencySec = (Date.now() - startTime) / 1000;
            this.emitMetrics(data?.statusCode, latencySec);

            if (data?.statusCode === RESPONSE_STATUS.STATUSCODE_200) {
                Logger.info(`WebexCallForwardService: CallForwardSettingsSuccess-200 (attempt ${attempt + 1}): done.`);
                removeLocalStorageItem(MAKE_A_CALL_KEYS.CALL_FORWARD_SETTING_INTERVAL);
                window.dispatchEvent(new Event("storage"));

                const next = this.parseCallForwardData(data.data?.callSetting?.destination as string);
                this.lastSuccessValue = next;
                if (this.observables$) {
                    this.observables$.next(next);
                }
                return;
            }

            // Non-200: retry transient codes, surface permanent ones immediately.
            if (this.shouldRetry(data?.statusCode, attempt)) {
                const delay = RETRY_BASE_DELAY_MS * Math.pow(2, attempt);
                Logger.info(`WebexCallForwardService: transient status '${data?.statusCode}', retry ${attempt + 1}/${MAX_RETRY_ATTEMPTS} in ${delay}ms`);
                await new Promise((f) => setTimeout(f, delay));
                return this.executeRefresh(attempt + 1);
            }

            if (data?.message === RESPONSE_STATUS.FAILURE) {
                Logger.error(`CallForwardSettings: got error: Status Code '${data?.statusCode}' after ${attempt + 1} attempt(s).`);
            } else {
                Logger.info(`WebexCallForwardService: non-success status '${data?.statusCode}' after ${attempt + 1} attempt(s); preserving last known value`);
            }
            setLocalStorageItem(MAKE_A_CALL_KEYS.CALL_FORWARD_SETTING_INTERVAL, true);
            window.dispatchEvent(new Event("storage"));
            this.preserveOrEmitInitial();
        } catch (err: any) {
            const latencySec = (Date.now() - startTime) / 1000;
            this.emitMetrics(undefined, latencySec);

            if (attempt < MAX_RETRY_ATTEMPTS) {
                const delay = RETRY_BASE_DELAY_MS * Math.pow(2, attempt);
                Logger.info(`WebexCallForwardService: getCallSetting threw '${formatError(err)}', retry ${attempt + 1}/${MAX_RETRY_ATTEMPTS} in ${delay}ms`);
                await new Promise((f) => setTimeout(f, delay));
                return this.executeRefresh(attempt + 1);
            }

            Logger.error(`CallForwardSettings: getCallSetting failed after ${attempt + 1} attempt(s): ${formatError(err)}`);
            setLocalStorageItem(MAKE_A_CALL_KEYS.CALL_FORWARD_SETTING_INTERVAL, true);
            window.dispatchEvent(new Event("storage"));
            this.preserveOrEmitInitial();
        }
    }

    /**
     * On final failure: re-emit the last known good value so the pill stays
     * stable instead of flipping to "Do not forward calls" on a transient error.
     * If we have never had a successful response, leave the observable untouched
     * — the UI's initial empty state is safer than a misleading wrong value.
     */
    private preserveOrEmitInitial(): void {
        if (!this.observables$) return;
        if (this.lastSuccessValue !== undefined) {
            Logger.info(`WebexCallForwardService: re-emitting last known value to keep UI consistent`);
            this.observables$.next(this.lastSuccessValue);
        }
    }

    /** True when the status indicates a transient failure and we still have retry budget. */
    private shouldRetry(statusCode: number | undefined, attempt: number): boolean {
        if (attempt >= MAX_RETRY_ATTEMPTS) return false;
        if (statusCode === undefined) return true;
        return RETRYABLE_STATUS_CODES.has(statusCode);
    }

    private emitMetrics(statusCode: number | undefined, latencySec: number): void {
        submitMetrics({
            metricEvent: CALL_FORWARDING.CallForwardSettingsLatency,
            callForwardSettingsLatency: `${latencySec}`,
        });
        submitMetrics({
            metricEvent: CALL_FORWARDING.CallForwardSettingsReturnCode,
            callForwardSettingsApiReturnCode: `${statusCode ?? 'network_error'}`,
        });
    }

    getCallForwardSettings(): Observable<string> {
        if (!this.observables$) {
            this.observables$ = new ReplaySubject<string>();
            this.refreshCallForward();
        }
        return this.observables$;
    }

    checkAttendantConsoleFeatureToggle(): Observable<boolean> {
       return from(getAttendantConsoleFeatuteToggle());
    }

    checkCXEssentialsFeature(): Observable<boolean> {
      return from(getCXEssentialsFeature());
   }
}
